"use client";

import { useMemo } from "react";
import { BarChart3 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import DailyAccuracyChart from "@/components/dashboard/DailyAccuracyChart";
import { ProblemHistory } from "@/lib/types";
import {
  ERROR_NOTE_CODING_SUBJECTS,
  ERROR_NOTE_SCOPE_DISPLAY_NAMES,
  ERROR_NOTE_SUBJECT_DISPLAY_NAMES,
  ErrorNoteSubjectStats,
  computeErrorNoteDailyStats,
  computeErrorNoteStatistics,
  computeErrorNoteTotalStats,
} from "@/lib/error-note-stats";

interface Props {
  histories: ProblemHistory[];
}

/**
 * 오답노트 통계 탭과 완전히 동일한 레이아웃. 호출자는 활성 회차/이전 회차의 `ProblemHistory[]`를
 * 넘기면 되고 내부 계산·렌더는 공유됨(`lib/error-note-stats.ts`).
 */
export default function ErrorNoteStatsView({ histories }: Props) {
  const statistics = useMemo(() => computeErrorNoteStatistics(histories), [histories]);
  const totalStats = useMemo(() => computeErrorNoteTotalStats(statistics), [statistics]);
  const dailyStats = useMemo(() => computeErrorNoteDailyStats(histories), [histories]);

  if (totalStats.totalAttempts === 0) {
    return (
      <div className="text-center py-12 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
        <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-gray-600 dark:text-gray-400">아직 풀이 이력이 없습니다.</p>
        <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
          문제를 풀면 통계를 확인할 수 있습니다.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 전체 통계 요약 */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold dark:text-white">전체 통계</span>
              <span
                className={`text-2xl font-bold ${
                  totalStats.accuracyRate >= 70
                    ? "text-green-500"
                    : totalStats.accuracyRate >= 50
                    ? "text-amber-500"
                    : "text-red-500 dark:text-red-400"
                }`}
              >
                정답률 {totalStats.accuracyRate.toFixed(1)}%
              </span>
            </div>
            <Progress value={totalStats.accuracyRate} className="h-3" />
            <div className="grid grid-cols-4 gap-4 text-center text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">푼 문제</p>
                <p className="font-semibold dark:text-white">
                  {totalStats.problemCount}개
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">총 시도</p>
                <p className="font-semibold dark:text-white">
                  {totalStats.totalAttempts}회
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">정답</p>
                <p className="font-semibold text-green-600">
                  {totalStats.correctCount}회
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">오답</p>
                <p className="font-semibold text-red-600">
                  {totalStats.incorrectCount}회
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 일자별 정답률 변화 차트 (본문은 공용 컴포넌트 — /my 대시보드와 공유, JCG-2073) */}
      {dailyStats.length > 1 && (
        <Card>
          <CardContent className="pt-6">
            <DailyAccuracyChart dailyStats={dailyStats} />
          </CardContent>
        </Card>
      )}

      {/* 과목별 통계 */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold dark:text-white">과목별 정답률</h3>
        {[
          {
            label: "이론",
            filter: (s: ErrorNoteSubjectStats) =>
              !ERROR_NOTE_CODING_SUBJECTS.has(s.subject),
          },
          {
            label: "코딩",
            filter: (s: ErrorNoteSubjectStats) =>
              ERROR_NOTE_CODING_SUBJECTS.has(s.subject),
          },
        ].map(({ label, filter }) => {
          const filtered = statistics.filter(filter);
          if (filtered.length === 0) return null;
          return (
            <div key={label} className="space-y-3">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {label}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map((subjectStat) => (
                  <Card key={subjectStat.subject}>
                    <CardContent className="pt-4 pb-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium dark:text-white">
                            {ERROR_NOTE_SUBJECT_DISPLAY_NAMES[subjectStat.subject] ||
                              subjectStat.subject}
                          </span>
                          <span
                            className={`text-lg font-semibold ${
                              subjectStat.accuracyRate >= 70
                                ? "text-green-500"
                                : subjectStat.accuracyRate >= 50
                                ? "text-amber-500"
                                : "text-red-500 dark:text-red-400"
                            }`}
                          >
                            {subjectStat.accuracyRate.toFixed(1)}%
                          </span>
                        </div>
                        <Progress value={subjectStat.accuracyRate} className="h-2" />
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {subjectStat.problemCount}개 문제 · {subjectStat.totalAttempts}회 시도 ·
                          정답 {subjectStat.correctCount}회 · 오답 {subjectStat.incorrectCount}회
                        </div>
                        {subjectStat.scopes.length > 1 && (
                          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
                            {subjectStat.scopes.map((scopeStat) => (
                              <div
                                key={scopeStat.scope}
                                className="flex items-center gap-3"
                              >
                                <span className="text-sm text-gray-600 dark:text-gray-400 w-28 flex-shrink-0">
                                  {ERROR_NOTE_SCOPE_DISPLAY_NAMES[scopeStat.scope] ||
                                    scopeStat.scope}
                                </span>
                                <div className="flex-1">
                                  <Progress
                                    value={scopeStat.accuracyRate}
                                    className="h-1.5"
                                  />
                                </div>
                                <span
                                  className={`text-sm font-medium w-14 text-right ${
                                    scopeStat.accuracyRate >= 70
                                      ? "text-green-500"
                                      : scopeStat.accuracyRate >= 50
                                      ? "text-amber-500"
                                      : "text-red-500 dark:text-red-400"
                                  }`}
                                >
                                  {scopeStat.accuracyRate.toFixed(1)}%
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
